import { useQuery, useMutation } from '@tanstack/react-query';
import { apiClient } from './api';

export interface Genre {
  id: number;
  name: string;
  slug: string;
  creator?: number;
  is_global: boolean | string;
}

export interface GenresResponse {
  success: boolean;
  message: string;
  data: Genre[];
  count: number;
}

export const useGetGenresQuery = (page: number = 1) => {
  return useQuery({
    queryKey: ['genres', page],
    queryFn: async () => {
      const { data } = await apiClient.get<GenresResponse>(`/custom_account/genres/?page=${page}`);
      return data;
    },
  });
};

export const useCreateGenreMutation = () => {
  return useMutation({
    mutationFn: async (name: string) => {
      const form = new FormData();
      form.append('name', name);
      const { data } = await apiClient.post<Genre>('/custom_account/genres/', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    },
  });
};

/**
 * Creates custom genres right after signup using the freshly issued token.
 * Returns only the slugs that were successfully created.
 */
export const createGenresAfterSignup = async (
  token: string,
  names: string[],
): Promise<string[]> => {
  if (!names.length) return [];
  const normalizedNames = Array.from(
    new Set(
      names
        .map((name) => (typeof name === 'string' ? name : '').trim())
        .filter((name) => name.length > 0),
    ),
  );
  if (!normalizedNames.length) return [];

  const results = await Promise.allSettled(
    normalizedNames.map((name) => {
      const form = new FormData();
      form.append('name', name);
      return apiClient.post<Genre>('/custom_account/genres/', form, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
      });
    }),
  );

  return results
    .filter((result): result is PromiseFulfilledResult<any> => result.status === 'fulfilled')
    .map((result) => {
      const respData = result.value.data;
      const genre = respData?.data ?? respData?.result ?? respData;
      return genre?.slug;
    })
    .filter((slug): slug is string => typeof slug === 'string' && slug.length > 0);
};
